import { useState, useEffect } from 'react';
import api from '../services/api';

// Human labels for the backend reason keys.
const REASON_LABELS = {
  too_expensive: 'Too expensive',
  not_using: 'Not using enough',
  found_better: 'Found a better app',
  exam_done: 'Exam done / different prep',
  other: 'Other',
};

const WINDOWS = [30, 90, 180];

function pct(n, d) {
  if (!d) return '—';
  return `${Math.round((n / d) * 100)}%`;
}

export default function DeletionFunnelPage() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      try {
        setLoading(true);
        setError(null);
        const result = await api.getDeletionFunnel(days);
        if (!cancelled) setData(result.data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchData();
    return () => { cancelled = true; };
  }, [days]);

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Error loading deletion funnel: {error}
        </div>
      </div>
    );
  }

  const offer = data?.offer || {};
  const byReason = data?.by_reason || {};
  const total = data?.total_intents || 0;
  const retentionRate = data?.retention_rate;

  return (
    <div className="p-6">
      {/* Header + window selector */}
      <div className="mb-6 flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-teacher-text-primary">Churn / Deletions</h1>
          <p className="text-teacher-text-secondary">
            Why users head to delete, and how often a save offer keeps them.
            Source: <code className="text-xs bg-teacher-bg-secondary px-1 rounded">deletion_intents</code>
          </p>
        </div>
        <div className="flex gap-1 bg-teacher-bg-secondary rounded-lg p-1">
          {WINDOWS.map((w) => (
            <button
              key={w}
              onClick={() => setDays(w)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                days === w
                  ? 'bg-white text-teacher-text-primary shadow-sm'
                  : 'text-teacher-text-secondary hover:text-teacher-text-primary'
              }`}
            >
              {w}d
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="h-48 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : total === 0 ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-teacher-border text-center text-teacher-text-tertiary">
          <p className="text-lg mb-2">No deletion activity in the last {days} days</p>
          <p className="text-sm">
            Nobody has started the delete flow yet — or the retention feature hasn't shipped to clients.
          </p>
        </div>
      ) : (
        <>
          {/* Summary metric cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <SummaryCard
              label="Started delete flow"
              value={total}
              sub={`last ${days} days`}
              accent="border-l-teacher-text-tertiary"
            />
            <SummaryCard
              label="Offered a save"
              value={offer.shown || 0}
              sub={`${pct(offer.shown || 0, total)} of starts`}
              accent="border-l-amber-500"
            />
            <SummaryCard
              label="Saved (offer accepted)"
              value={offer.accepted || 0}
              sub={`${pct(offer.accepted || 0, offer.shown || 0)} of offered`}
              accent="border-l-green-500"
            />
            <SummaryCard
              label="Deleted anyway"
              value={offer.deleted || 0}
              sub={`${pct(offer.deleted || 0, total)} of starts`}
              accent="border-l-red-500"
            />
          </div>

          {/* Retention rate banner */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-teacher-border mb-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h3 className="text-sm font-medium text-teacher-text-secondary mb-1">
                  Save Rate (of users who reached a decision)
                </h3>
                <p className="text-xs text-teacher-text-tertiary">
                  Accepted ÷ (accepted + deleted). Abandoned-mid-flow intents excluded.
                </p>
              </div>
              <span className="text-4xl font-bold text-teacher-text-primary">
                {retentionRate != null ? `${Math.round(retentionRate * 100)}%` : '—'}
              </span>
            </div>
            {/* Outcome breakdown bar */}
            <div className="mt-4 flex h-3 rounded-full overflow-hidden bg-teacher-bg-secondary">
              <Segment count={offer.accepted} total={total} className="bg-green-500" />
              <Segment count={offer.declined} total={total} className="bg-amber-400" />
              <Segment count={offer.deleted} total={total} className="bg-red-500" />
              <Segment count={offer.abandoned} total={total} className="bg-gray-300" />
            </div>
            <div className="mt-2 flex flex-wrap gap-4 text-xs text-teacher-text-secondary">
              <Legend className="bg-green-500" label={`Accepted (${offer.accepted || 0})`} />
              <Legend className="bg-amber-400" label={`Declined (${offer.declined || 0})`} />
              <Legend className="bg-red-500" label={`Deleted (${offer.deleted || 0})`} />
              <Legend className="bg-gray-300" label={`Abandoned (${offer.abandoned || 0})`} />
            </div>
          </div>

          {/* By-reason table */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-teacher-border">
            <h3 className="text-lg font-semibold text-teacher-text-primary mb-4">By Reason</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-teacher-border">
                    <th className="text-left py-2 px-3 text-xs font-semibold text-teacher-text-secondary uppercase">Reason</th>
                    <th className="text-center py-2 px-3 text-xs font-semibold text-teacher-text-secondary uppercase">Started</th>
                    <th className="text-center py-2 px-3 text-xs font-semibold text-teacher-text-secondary uppercase">Offered</th>
                    <th className="text-center py-2 px-3 text-xs font-semibold text-teacher-text-secondary uppercase">Accepted</th>
                    <th className="text-center py-2 px-3 text-xs font-semibold text-teacher-text-secondary uppercase">Deleted</th>
                    <th className="text-center py-2 px-3 text-xs font-semibold text-teacher-text-secondary uppercase">Save rate</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(REASON_LABELS).map((key) => {
                    const r = byReason[key] || { count: 0, offer_shown: 0, accepted: 0, deleted: 0 };
                    const decided = (r.accepted || 0) + (r.deleted || 0);
                    return (
                      <tr key={key} className="border-b border-gray-50">
                        <td className="py-2 px-3 font-medium text-teacher-text-primary">{REASON_LABELS[key]}</td>
                        <td className="py-2 px-3 text-center text-teacher-text-secondary">{r.count || 0}</td>
                        <td className="py-2 px-3 text-center text-teacher-text-secondary">{r.offer_shown || 0}</td>
                        <td className="py-2 px-3 text-center text-green-600 font-semibold">{r.accepted || 0}</td>
                        <td className="py-2 px-3 text-center text-red-600 font-semibold">{r.deleted || 0}</td>
                        <td className="py-2 px-3 text-center">
                          <span className="text-teacher-text-primary font-semibold">{pct(r.accepted || 0, decided)}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-teacher-text-tertiary mt-4">
              "Save rate" = accepted ÷ (accepted + deleted) for that reason. Reasons with no offer
              (e.g. "Other", or free-tier users) show 0 offered by design.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value, sub, accent }) {
  return (
    <div className={`rounded-xl p-6 shadow-sm border border-teacher-border border-l-4 ${accent} bg-white`}>
      <h3 className="text-sm font-medium text-teacher-text-secondary mb-1">{label}</h3>
      <span className="text-3xl font-bold text-teacher-text-primary">{value}</span>
      <p className="text-xs text-teacher-text-tertiary mt-1">{sub}</p>
    </div>
  );
}

function Segment({ count, total, className }) {
  if (!count || !total) return null;
  return <div className={className} style={{ width: `${(count / total) * 100}%` }} />;
}

function Legend({ className, label }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className={`inline-block w-2.5 h-2.5 rounded-sm ${className}`} />
      {label}
    </span>
  );
}
